const activeSectionChangeEventName = 'section-keyboard:active-section-change';
const entryDelayMs = 750;
const debugVariantParam = new URLSearchParams(window.location.search).get('sinopse-debug');
const debugVariant = debugVariantParam && /^[abc]$/i.test(debugVariantParam)
  ? debugVariantParam.toLowerCase()
  : null;

const sinopseSections = new Map();
const desktopMediaQuery = window.matchMedia('(min-width: 900px)');
const reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

function isVisible(element) {
  return Boolean(element && element.offsetParent !== null);
}

function warmupImage(image) {
  if (!image) {
    return Promise.resolve();
  }

  if (image.complete && image.naturalWidth > 0) {
    return Promise.resolve();
  }

  if (typeof image.decode === 'function') {
    return image.decode().catch(() => {});
  }

  return new Promise((resolve) => {
    image.addEventListener('load', resolve, { once: true });
    image.addEventListener('error', resolve, { once: true });
  });
}

function nextFrame() {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

function waitMs(durationMs) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, durationMs);
  });
}

function getCssNumber(element, propertyName) {
  const value = window.getComputedStyle(element).getPropertyValue(propertyName).trim();
  const parsedValue = Number.parseFloat(value);

  if (!Number.isFinite(parsedValue)) {
    return 0;
  }

  return parsedValue;
}

function setBackgroundBaseTransform(backgroundImage) {
  if (!backgroundImage) {
    return;
  }

  backgroundImage.style.transform = '';
}

function shouldFloat() {
  return debugVariant !== 'b';
}

function sampleAnimationFps(label) {
  const durationMs = 1600;
  let startedAt = 0;
  let lastTick = 0;
  let frames = 0;
  let slowFrames = 0;

  function tick(now) {
    if (!startedAt) {
      startedAt = now;
      lastTick = now;
    }

    const delta = now - lastTick;
    lastTick = now;
    frames += 1;

    if (delta > 25) {
      slowFrames += 1;
    }

    if (now - startedAt < durationMs) {
      window.requestAnimationFrame(tick);
      return;
    }

    const elapsed = now - startedAt;
    const averageFps = elapsed > 0 ? (frames * 1000) / elapsed : 0;

    if (!window.__sinopseDebugMetrics) {
      window.__sinopseDebugMetrics = {};
    }

    window.__sinopseDebugMetrics[label] = {
      averageFps,
      elapsed,
      frames,
      slowFrames,
      timestamp: Date.now(),
    };

    console.info(`[sinopse-debug ${label}] fps medio ${averageFps.toFixed(1)} | frames lentos ${slowFrames}`);
  }

  window.requestAnimationFrame(tick);
}

function setEnteredState(section, pintinhoInner) {
  section.classList.remove('sinopse--ready', 'sinopse--entering', 'sinopse--text-ready');
  section.classList.add('sinopse--entered', 'sinopse--text-entered');
  section.classList.toggle('sinopse--floating', shouldFloat());
  pintinhoInner.style.transform = 'scale(1)';
}

function setReadyState(section, pintinhoInner) {
  section.classList.remove('sinopse--entered', 'sinopse--entering', 'sinopse--floating', 'sinopse--text-entered');
  section.classList.add('sinopse--ready', 'sinopse--text-ready');
  pintinhoInner.style.transform = 'scale(0)';
}

function cancelRunningEntry(state) {
  if (state.entryRaf) {
    cancelAnimationFrame(state.entryRaf);
    state.entryRaf = null;
  }

  if (state.growAnimation) {
    state.growAnimation.cancel();
    state.growAnimation = null;
  }
}

function mountSinopse(section) {
  const backgroundImage = section.querySelector('.sinopse__imagem img');
  const pintinhoInner = section.querySelector('.sinopse__pintinho-inner');
  const pintinhoImage = section.querySelector('.sinopse__pintinho-amarelinho img');
  const textContent = section.querySelector('.sinopse__texto');

  if (!backgroundImage || !pintinhoInner || !pintinhoImage || !textContent) {
    return;
  }

  const state = {
    backgroundDriftAmplitudeX: 0,
    backgroundDriftAmplitudeY: 0,
    backgroundScale: 1,
    entryRaf: null,
    entryRunId: 0,
    growAnimation: null,
    hasEnteredOnce: false,
    imageReady: false,
    isDesktopActive: false,
    mobilePlayed: false,
    observer: null,
    pendingEntry: false,
    pendingReplay: false,
    probeRunning: false,
    backgroundDriftRaf: null,
    backgroundDriftActive: false,
    backgroundDriftStartedAt: 0,
  };

  function stopBackgroundDrift() {
    if (state.backgroundDriftRaf) {
      cancelAnimationFrame(state.backgroundDriftRaf);
      state.backgroundDriftRaf = null;
    }

    state.backgroundDriftActive = false;
    state.backgroundDriftStartedAt = 0;
    setBackgroundBaseTransform(backgroundImage);
  }

  function tickBackgroundDrift(now) {
    if (!state.backgroundDriftActive) {
      return;
    }

    if (!state.backgroundDriftStartedAt) {
      state.backgroundDriftStartedAt = now;
    }

    const elapsed = now - state.backgroundDriftStartedAt;
    const cycleDurationMs = 36000;
    const phase = (elapsed / cycleDurationMs) * Math.PI * 2 * 3;
    const x = state.backgroundDriftAmplitudeX * Math.sin(phase);
    const y = state.backgroundDriftAmplitudeY * Math.sin(phase * 2);

    backgroundImage.style.transform = `translate3d(${x.toFixed(3)}%, ${y.toFixed(3)}%, 0) scale(${state.backgroundScale})`;
    state.backgroundDriftRaf = window.requestAnimationFrame(tickBackgroundDrift);
  }

  function startBackgroundDrift() {
    if (state.backgroundDriftActive) {
      return;
    }

    state.backgroundDriftActive = true;
    state.backgroundDriftStartedAt = 0;
    state.backgroundDriftRaf = window.requestAnimationFrame(tickBackgroundDrift);
  }

  function syncBackgroundDrift() {
    state.backgroundDriftAmplitudeX = getCssNumber(section, '--sinopse-background-drift-x-amplitude');
    state.backgroundDriftAmplitudeY = getCssNumber(section, '--sinopse-background-drift-y-amplitude');
    state.backgroundScale = getCssNumber(section, '--sinopse-background-scale') || 1;

    const shouldAnimateBackground = desktopMediaQuery.matches
      && !reducedMotionMediaQuery.matches
      && state.isDesktopActive
      && isVisible(section)
      && (state.backgroundDriftAmplitudeX > 0 || state.backgroundDriftAmplitudeY > 0);

    if (!shouldAnimateBackground) {
      stopBackgroundDrift();
      return;
    }

    startBackgroundDrift();
  }

  section.classList.remove('sinopse--debug-a', 'sinopse--debug-b', 'sinopse--debug-c');

  if (debugVariant) {
    section.classList.add(`sinopse--debug-${debugVariant}`);
  }

  async function runEntryAnimation() {
    const currentRunId = ++state.entryRunId;

    cancelRunningEntry(state);
    setReadyState(section, pintinhoInner);
    await nextFrame();

    if (currentRunId !== state.entryRunId || !isVisible(section)) {
      return;
    }

    await waitMs(entryDelayMs);

    if (currentRunId !== state.entryRunId || !isVisible(section)) {
      return;
    }

    section.classList.remove('sinopse--ready');
    section.classList.add('sinopse--entering');

    if (debugVariant && !state.probeRunning) {
      state.probeRunning = true;
      sampleAnimationFps(debugVariant);
      window.setTimeout(() => {
        state.probeRunning = false;
      }, 1700);
    }

    if (typeof pintinhoInner.animate !== 'function') {
      pintinhoInner.style.transform = 'scale(1)';
      setEnteredState(section, pintinhoInner);
      return;
    }

    state.growAnimation = pintinhoInner.animate([
      { transform: 'translate3d(6%, -6%, 0) scale(0)', offset: 0 },
      { transform: 'translate3d(3.8%, -4.2%, 0) scale(0.38)', offset: 0.22 },
      { transform: 'translate3d(1%, -0.8%, 0) scale(0.7)', offset: 0.5 },
      { transform: 'translate3d(-1.2%, 1.6%, 0) scale(0.92)', offset: 0.76 },
      { transform: 'translate3d(0.45%, 0.6%, 0) scale(1.04)', offset: 0.9 },
      { transform: 'translate3d(0, 0, 0) scale(1)', offset: 1 },
    ], {
      duration: 1680,
      easing: 'cubic-bezier(0.2, 0.74, 0.2, 1)',
      fill: 'forwards',
    });

    try {
      await state.growAnimation.finished;
    } catch {
      return;
    }

    if (currentRunId !== state.entryRunId) {
      return;
    }

    state.growAnimation = null;
    state.hasEnteredOnce = true;
    setEnteredState(section, pintinhoInner);
  }

  function triggerEntryAnimation(replay = false) {
    if (reducedMotionMediaQuery.matches) {
      state.hasEnteredOnce = true;
      setEnteredState(section, pintinhoInner);
      return;
    }

    if (state.hasEnteredOnce) {
      setEnteredState(section, pintinhoInner);
      return;
    }

    if (!replay && section.classList.contains('sinopse--entered')) {
      return;
    }

    if (!state.imageReady) {
      state.pendingEntry = true;
      state.pendingReplay = state.pendingReplay || replay;
      section.classList.add('sinopse--ready', 'sinopse--text-ready');
      section.classList.remove('sinopse--floating');
      return;
    }

    runEntryAnimation();
  }

  function flushPendingEntry() {
    if (!state.pendingEntry) {
      return;
    }

    const replay = state.pendingReplay;
    state.pendingEntry = false;
    state.pendingReplay = false;
    triggerEntryAnimation(replay);
  }

  function handleDesktopSectionChange(event) {
    const activeSection = event.detail && event.detail.section;

    state.isDesktopActive = activeSection === section;
    syncBackgroundDrift();

    if (!desktopMediaQuery.matches || activeSection !== section || !isVisible(section)) {
      return;
    }

    triggerEntryAnimation(false);
  }

  function mountObserver() {
    if (state.observer) {
      return;
    }

    state.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (desktopMediaQuery.matches || state.mobilePlayed) {
          return;
        }

        if (!entry.isIntersecting || entry.intersectionRatio < 0.42) {
          return;
        }

        state.mobilePlayed = true;
        triggerEntryAnimation(false);
      });
    }, {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: [0.42, 0.65],
    });

    state.observer.observe(section);
  }

  function unmountObserver() {
    if (!state.observer) {
      return;
    }

    state.observer.disconnect();
    state.observer = null;
  }

  function updateByViewport() {
    if (reducedMotionMediaQuery.matches) {
      unmountObserver();
      cancelRunningEntry(state);
      stopBackgroundDrift();
      setEnteredState(section, pintinhoInner);
      return;
    }

    if (desktopMediaQuery.matches) {
      unmountObserver();

      if (!section.classList.contains('sinopse--entered')) {
        section.classList.add('sinopse--ready', 'sinopse--text-ready');
      }

      flushPendingEntry();
      syncBackgroundDrift();
      return;
    }

    stopBackgroundDrift();
    mountObserver();

    if (!state.mobilePlayed && !section.classList.contains('sinopse--entered')) {
      section.classList.add('sinopse--ready', 'sinopse--text-ready');
      section.classList.remove('sinopse--floating', 'sinopse--text-entered');
    }
  }

  document.addEventListener(activeSectionChangeEventName, handleDesktopSectionChange);
  desktopMediaQuery.addEventListener('change', updateByViewport);
  reducedMotionMediaQuery.addEventListener('change', updateByViewport);

  sinopseSections.set(section, {
    updateByViewport,
    destroy: () => {
      unmountObserver();
      cancelRunningEntry(state);
      stopBackgroundDrift();
      document.removeEventListener(activeSectionChangeEventName, handleDesktopSectionChange);
      desktopMediaQuery.removeEventListener('change', updateByViewport);
      reducedMotionMediaQuery.removeEventListener('change', updateByViewport);
      pintinhoInner.style.transform = '';
    },
  });

  updateByViewport();

  warmupImage(pintinhoImage).then(() => {
    state.imageReady = true;
    flushPendingEntry();
  });

  if (reducedMotionMediaQuery.matches) {
    setEnteredState(section, pintinhoInner);
  }

  const activeDesktopSection = document.querySelector('#page-slider .splide__slide.is-active section');

  state.isDesktopActive = activeDesktopSection === section;
  syncBackgroundDrift();

  if (desktopMediaQuery.matches && activeDesktopSection === section) {
    triggerEntryAnimation(false);
  }
}

export function syncSinopse() {
  const sections = document.querySelectorAll('.sinopse');

  sections.forEach((section) => {
    const instance = sinopseSections.get(section);

    if (isVisible(section) && !instance) {
      mountSinopse(section);
      return;
    }

    if (!isVisible(section) && instance) {
      instance.destroy();
      sinopseSections.delete(section);
      section.classList.remove('sinopse--ready', 'sinopse--entering', 'sinopse--floating', 'sinopse--text-ready', 'sinopse--text-entered');
    }
  });

  sinopseSections.forEach((instance, section) => {
    if (!section.isConnected) {
      instance.destroy();
      sinopseSections.delete(section);
      return;
    }

    instance.updateByViewport();
  });
}
